import { ComponentProps } from 'react';
import { TextButtonVariants, textButton } from './variants';
import { match } from 'ts-pattern';
import * as React from 'react';
import { createLink, LinkComponent } from '@tanstack/react-router';
import {
  mergeProps,
  useFocusRing,
  useHover,
  useLink,
  useObjectRef,
} from 'react-aria';
import type { AriaLinkOptions } from 'react-aria';

import { twMerge } from 'tailwind-merge';
import { ReactNode, SVGProps } from 'react';
import { Typography } from '../typography/typography';

export type LinkTextButtonProps = {
  children?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  iconLeft?: (props: SVGProps<SVGSVGElement>) => ReactNode;
  iconRight?: (props: SVGProps<SVGSVGElement>) => ReactNode;
} & Omit<AriaLinkOptions, 'href'> &
  TextButtonVariants;

const RACLinkComponent = React.forwardRef<
  HTMLAnchorElement,
  LinkTextButtonProps
>((props, forwardedRef) => {
  const {
    iconLeft: IconLeft,
    iconRight: IconRight,
    className,
    size,
    intent,
    ...rest
  } = props;
  const ref = useObjectRef(forwardedRef);

  const { isPressed, linkProps } = useLink(rest, ref);
  const { isHovered, hoverProps } = useHover(rest);
  const { isFocusVisible, isFocused, focusProps } = useFocusRing(rest);

  return (
    <a
      {...mergeProps(linkProps, hoverProps, focusProps, rest)}
      ref={ref}
      data-hovered={isHovered || undefined}
      data-pressed={isPressed || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-focused={isFocused || undefined}
      aria-disabled={rest?.isDisabled}
      data-disabled={rest.isDisabled}
      className={twMerge(textButton({ intent, size }), className)}
    >
      {IconLeft && <IconLeft className={mapSizeToIconVariant(size)} />}

      <Typography
        as="span"
        variant={mapSizeToTypographyVariant(size)}
        weight={mapSizeToTypographyWeight(size)}
        className="text-currentColor"
      >
        {rest.children}
      </Typography>

      {IconRight && <IconRight className={mapSizeToIconVariant(size)} />}
    </a>
  );
});

function mapSizeToTypographyVariant(
  size: TextButtonVariants['size'],
): ComponentProps<typeof Typography>['variant'] {
  return match(size)
    .returnType<ComponentProps<typeof Typography>['variant']>()
    .with('tiny', () => 'body-xs')
    .with('small', () => 'body-s')
    .with('default', () => 'body-m')
    .otherwise(() => 'body-m');
}

function mapSizeToTypographyWeight(
  size: TextButtonVariants['size'],
): ComponentProps<typeof Typography>['weight'] {
  return match(size)
    .returnType<ComponentProps<typeof Typography>['weight']>()
    .with('tiny', () => 'medium')
    .with('small', () => 'medium')
    .with('default', () => 'medium')
    .otherwise(() => 'medium');
}

function mapSizeToIconVariant(size: TextButtonVariants['size']): string {
  return match(size)
    .returnType<string>()
    .with('tiny', () => 'min-w-16 w-16 h-16')
    .with('small', () => 'min-w-20 w-20 h-20')
    .with('default', () => 'min-w-24 w-24 h-24')
    .otherwise(() => 'min-w-24 w-24 h-24');
}

const CreatedLinkComponent = createLink(RACLinkComponent);

export const TextButtonLink: LinkComponent<typeof RACLinkComponent> = (
  props,
) => {
  return <CreatedLinkComponent preload="intent" {...props} />;
};
